import { CHAT_TITLE_MAX, cleanChatTitle } from '@/hooks/chat-titles.store';
import { cn } from '@/lib/utils';
import { useLayoutEffect, useRef, type KeyboardEvent } from 'react';

/**
 * A chat's title as a text field, in the title's place: it opens with the
 * title selected. Enter or leaving the field saves, Escape cancels, and an
 * empty or unchanged title changes nothing. The Enter or Escape that ends an
 * IME composition is the composition's. `onDone` gets the new title, or
 * null when nothing changes, and whether a key ended it (Enter or Escape),
 * which gives focus back to the row.
 */
export function ChatTitleField({
  title,
  onDone,
  className,
}: {
  title: string;
  onDone: (next: string | null, byKey: boolean) => void;
  className?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const done = useRef(false);
  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    input.select();
  }, []);

  const finish = (value: string | null, byKey: boolean) => {
    if (done.current) return;
    done.current = true;
    const next = value === null ? '' : cleanChatTitle(value);
    onDone(next && next !== cleanChatTitle(title) ? next : null, byKey);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // The row's own keys (its menu, the rail's arrows, a drag) wait.
    event.stopPropagation();
    // An IME's candidate is confirmed or dropped with Enter or Escape: while
    // composing (Chrome, Firefox), or as key code 229 once Safari has ended
    // the composition.
    if (event.nativeEvent.isComposing || event.keyCode === 229) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      finish(event.currentTarget.value, true);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      finish(null, true);
    }
  };

  return (
    <input
      ref={inputRef}
      type='text'
      aria-label='Chat title'
      defaultValue={title}
      maxLength={CHAT_TITLE_MAX}
      spellCheck={false}
      autoComplete='off'
      onKeyDown={onKeyDown}
      onBlur={(event) => finish(event.currentTarget.value, false)}
      className={cn(
        'min-w-0 flex-1 rounded-md bg-background px-1.5 text-foreground ring-1 ring-primary/60 outline-hidden',
        'selection:bg-primary/25 dark:bg-white/[0.06]',
        className
      )}
    />
  );
}
